import {
  Box,
  Container,
  Fade,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * @typedef {Object} HeroSectionProps
 * @description Props for the HeroSection component
 * @property {string} [className] - Additional CSS classes
 * @property {Object} [style] - Inline styles
 * @property {string} [title] - Custom hero title (defaults to "Ready Secure Managed Cloud")
 * @property {string} [subtitle] - Custom hero subtitle
 * @property {string} [videoSrc] - Custom video source URL (defaults to S3-hosted optimized video)
 * @property {string} [posterSrc] - Video poster image URL (defaults to local poster image)
 */

/**
 * Hero section component that displays the main value proposition with video background.
 *
 * Features include:
 * - Video background that plays once and stops at the final frame
 * - Responsive design with mobile detection
 * - Auto-playing video with cross-browser compatibility
 * - Overlay text with the main value proposition
 * - Optimized video playback for different screen sizes
 *
 * @param {HeroSectionProps} [props] - Component props
 * @returns {JSX.Element} Hero section with video background and overlay text
 */
function HeroSection({
  className = '',
  style = {},
  title = 'Ready Secure Managed Cloud',
  subtitle = 'Leading organizations through strategic technology transformation',
  // Production S3-hosted video from Adobe Stock (AdobeStock_1523325914)
  // Original: 62MB MOV -> Optimized: 4.1MB MP4 slowed to 1.5x speed (0.67x playback)
  // Video plays once and stops at the final frame
  // H.264 codec with fast-start enabled for immediate playback
  // Hosted on S3 for optimal delivery and performance
  videoSrc = 'https://rsm-collective-media.s3.us-east-2.amazonaws.com/hero-video-11.mp4',
  posterSrc = '/videos/hero-video-poster.jpg',
}) {
  const [isMobile, setIsMobile] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState(false);

  const theme = useTheme();
  const isMobileBreakpoint = useMediaQuery(theme.breakpoints.down('md'));
  const heroRef = useRef(null);
  const videoRef = useRef(null);

  /**
   * Handle mouse movement for parallax effects
   */
  const handleMouseMove = useCallback(
    (e) => {
      if (!isMobile && heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
        const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
        setMousePosition({ x: x * 100, y: y * 100 });
      }
    },
    [isMobile]
  );

  /**
   * Handle scroll for parallax effects
   */
  const handleScroll = useCallback(() => {
    setScrollY(window.scrollY);
  }, []);

  /**
   * Effect to detect mobile devices and update state accordingly
   * Sets up resize listener to handle responsive behavior
   */
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  /**
   * Effect to set up scroll and mouse event listeners
   */
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    if (!isMobile) {
      window.addEventListener('mousemove', handleMouseMove, { passive: true });
    }

    // Set loaded state immediately so glass effects appear on first render
    setIsLoaded(true);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [handleScroll, handleMouseMove, isMobile]);

  return (
    <Box
      ref={heroRef}
      className={className}
      style={style}
      sx={{
        position: 'relative',
        borderTop: '4px solid',
        borderTopColor: 'primary.main',
        borderBottom: '3px solid',
        borderBottomColor: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '70vh',
        maxHeight: '70vh',
        overflow: 'hidden',
        backgroundColor: '#000000',
        color: 'var(--color-secondary-text)',
        textAlign: 'center',
        px: 2,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            'linear-gradient(45deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.4) 50%, rgba(0, 0, 0, 0.2) 100%)',
          zIndex: 1,
          pointerEvents: 'none',
        },
      }}
    >
      {/* Video Background */}
      <Box
        component="video"
        ref={videoRef}
        autoPlay
        muted
        playsInline
        preload="auto"
        poster={posterSrc}
        sx={{
          position: 'absolute',
          top: isMobile ? 0 : `${scrollY * 0.3}px`,
          left: 0,
          width: '100%',
          height: isMobile ? '100%' : '110%',
          objectFit: 'cover',
          zIndex: 0,
          transition: 'transform 0.1s ease-out',
          transform: isMobile
            ? 'none'
            : `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px) scale(1.05)`,
        }}
      >
        <source src={videoSrc} type="video/mp4" />
      </Box>
      {/* Hero Content Overlay */}
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 2,
          maxWidth: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          px: 2,
          overflow: 'hidden',
        }}
      >
        <Fade in={isLoaded} timeout={2000}>
          <Typography
            variant="h1"
            component="h1"
            sx={{
              mb: 3,
              color: 'var(--color-secondary-text)',
              textShadow:
                '0px 4px 12px rgba(0, 0, 0, 0.7), 0px 2px 4px rgba(0, 0, 0, 0.5)',
              opacity: isLoaded ? 1 : 0,
              transform: isLoaded
                ? 'translateY(0) scale(1)'
                : 'translateY(30px) scale(0.95)',
              transition: 'all 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: '0.5s',
              // Keep the headline on a single line at every breakpoint
              whiteSpace: 'nowrap',
              overflow: 'visible',
              textOverflow: 'unset',
              fontSize: isMobileBreakpoint
                ? 'clamp(1.1rem, 5.4vw, 2.4rem)'
                : 'clamp(2rem, 5vw, 4rem)',
              fontWeight: 600,
              letterSpacing: isMobileBreakpoint ? '0.01em' : '0.02em',
              fontFamily: '"Playfair Display", serif',
              lineHeight: 1.2,
              // Bars above and below header text only
              borderTop: '4px solid rgba(255, 255, 255, 0.4)',
              borderBottom: '4px solid rgba(255, 255, 255, 0.4)',
              borderLeft: 'none',
              borderRight: 'none',
              borderRadius: '0px',
              padding: '32px 24px',
              backdropFilter: 'blur(10px)',
              background: 'rgba(0, 0, 0, 0.2)',
              position: 'relative',
              '@keyframes borderFlow': {
                '0%': { backgroundPosition: '0% 50%' },
                '100%': { backgroundPosition: '200% 50%' },
              },
            }}
          >
            {title}
          </Typography>
        </Fade>

        {subtitle && (
          <Fade in={isLoaded} timeout={2500}>
            <Typography
              variant="h4"
              component="p"
              sx={{
                color: 'rgba(255, 255, 255, 0.95)',
                textShadow: '0px 2px 8px rgba(0, 0, 0, 0.5)',
                opacity: isLoaded ? 1 : 0,
                transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
                transition: 'all 1s ease-out',
                transitionDelay: '1.2s',
                mb: 4,
                fontSize: isMobileBreakpoint ? '1.2rem' : '1.5rem',
                fontWeight: 500,
                fontFamily: '"Manrope", "Roboto", sans-serif',
                letterSpacing: '0.02em',
                lineHeight: 1.5,
                maxWidth: '800px',
                mx: 'auto',
              }}
            >
              {subtitle}
            </Typography>
          </Fade>
        )}
      </Container>
    </Box>
  );
}

export default HeroSection;
